// store/admin/exam-management/store/_saga.js
import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from './_actionTypes';
import * as actions from './_actions';
import AdminExamHelper from '@/utils/API/BackendHelper/admin/exam';

// Get all exams
function* getAllExamsSaga(action) {
  try {
    const response = yield call([AdminExamHelper, 'getAllExams'], action.payload || {});
    
    if (response.success) {
      const data = response.data || [];
      const pagination = response.pagination || {
        page: 1,
        limit: data.length,
        total: data.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
      };
      yield put(actions.getAllExamsSuccess({ data, pagination }));
    } else {
      yield put(actions.getAllExamsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getAllExamsFailure(error.message));
  }
}

// Get exam by ID
function* getExamByIdSaga(action) {
  try {
    const { examId } = action.payload;
    const response = yield call([AdminExamHelper, 'getExamById'], examId);
    
    if (response.success) {
      yield put(actions.getExamByIdSuccess(response.data));
    } else {
      yield put(actions.getExamByIdFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getExamByIdFailure(error.message));
  }
}

// Create exam
function* createExamSaga(action) {
  try {
    const response = yield call([AdminExamHelper, 'createExam'], action.payload);
    
    if (response.success) {
      yield put(actions.createExamSuccess(response.data));
      // Optionally refresh the exam list
      yield put(actions.getAllExams());
    } else {
      yield put(actions.createExamFailure(response.message));
    }
  } catch (error) {
    yield put(actions.createExamFailure(error.message));
  }
}

// Update exam
function* updateExamSaga(action) {
  try {
    const { examId, updateData } = action.payload;
    const response = yield call([AdminExamHelper, 'updateExam'], examId, updateData);
    
    if (response.success) {
      yield put(actions.updateExamSuccess(response.data));
    } else {
      yield put(actions.updateExamFailure(response.message));
    }
  } catch (error) {
    yield put(actions.updateExamFailure(error.message));
  }
}

// Delete exam
function* deleteExamSaga(action) {
  try {
    const { examId } = action.payload;
    const response = yield call([AdminExamHelper, 'deleteExam'], examId);
    
    if (response.success) {
      yield put(actions.deleteExamSuccess(examId));
    } else {
      yield put(actions.deleteExamFailure(response.message));
    }
  } catch (error) {
    yield put(actions.deleteExamFailure(error.message));
  }
}

// Toggle exam status
function* toggleExamStatusSaga(action) {
  try {
    const { examId, isActive } = action.payload;
    const response = yield call([AdminExamHelper, 'toggleExamStatus'], examId, isActive);
    
    if (response.success) {
      yield put(actions.toggleExamStatusSuccess(response.data));
    } else {
      yield put(actions.toggleExamStatusFailure(response.message));
    }
  } catch (error) {
    yield put(actions.toggleExamStatusFailure(error.message));
  }
}

// Declare exam results
function* declareExamResultsSaga(action) {
  try {
    const { examId, resultsDeclared } = action.payload;
    const response = yield call([AdminExamHelper, 'declareExamResults'], examId, resultsDeclared);
    
    if (response.success) {
      yield put(actions.declareExamResultsSuccess(response.data));
      // Refresh exam list to show updated results status
      yield put(actions.getAllExams());
    } else {
      yield put(actions.declareExamResultsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.declareExamResultsFailure(error.message));
  }
}

// Get exam attempts
function* getExamAttemptsSaga(action) {
  try {
    const { examId } = action.payload;
    const response = yield call([AdminExamHelper, 'getExamAttempts'], examId);
    
    if (response.success) {
      yield put(actions.getExamAttemptsSuccess(response.data));
    } else {
      yield put(actions.getExamAttemptsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getExamAttemptsFailure(error.message));
  }
}

// Get exam analytics (live)
function* getExamAnalyticsSaga(action) {
  try {
    const { examId } = action.payload;
    const response = yield call([AdminExamHelper, 'getExamAnalytics'], examId);

    if (response.success) {
      yield put(actions.getExamAnalyticsSuccess(response.data));
    } else {
      yield put(actions.getExamAnalyticsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.getExamAnalyticsFailure(error.message));
  }
}

// Add exam questions
function* addExamQuestionsSaga(action) {
  try {
    const { examId, questions } = action.payload;
    const response = yield call([AdminExamHelper, 'addExamQuestions'], examId, questions);
    
    if (response.success) {
      yield put(actions.addExamQuestionsSuccess(response.data));
      // Refresh exam details to show updated questions
      yield put(actions.getExamById(examId));
    } else {
      yield put(actions.addExamQuestionsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.addExamQuestionsFailure(error.message));
  }
}

// Update exam questions
function* updateExamQuestionsSaga(action) {
  try {
    const { examId, questions } = action.payload;
    const response = yield call([AdminExamHelper, 'updateExamQuestions'], examId, questions);
    
    if (response.success) {
      yield put(actions.updateExamQuestionsSuccess(response.data));
      // Refresh exam details to show updated questions
      yield put(actions.getExamById(examId));
    } else {
      yield put(actions.updateExamQuestionsFailure(response.message));
    }
  } catch (error) {
    yield put(actions.updateExamQuestionsFailure(error.message));
  }
}

// Watcher saga
export default function* adminExamSaga() {
  yield takeLatest(types.GET_ALL_EXAMS_REQUEST, getAllExamsSaga);
  yield takeLatest(types.GET_EXAM_BY_ID_REQUEST, getExamByIdSaga);
  yield takeLatest(types.CREATE_EXAM_REQUEST, createExamSaga);
  yield takeLatest(types.UPDATE_EXAM_REQUEST, updateExamSaga);
  yield takeLatest(types.DELETE_EXAM_REQUEST, deleteExamSaga);
  yield takeLatest(types.TOGGLE_EXAM_STATUS_REQUEST, toggleExamStatusSaga);
  yield takeLatest(types.DECLARE_EXAM_RESULTS_REQUEST, declareExamResultsSaga);
  yield takeLatest(types.GET_EXAM_ATTEMPTS_REQUEST, getExamAttemptsSaga);
  yield takeLatest(types.GET_EXAM_ANALYTICS_REQUEST, getExamAnalyticsSaga);
  yield takeLatest(types.ADD_EXAM_QUESTIONS_REQUEST, addExamQuestionsSaga);
  yield takeLatest(types.UPDATE_EXAM_QUESTIONS_REQUEST, updateExamQuestionsSaga);
}